import { useMemo } from 'react';
import { VisibilityMap } from '../../types/VisibilityTypes';
import { TransientFunnel } from './funnelMapCanvasTypes';
import { PillsMap } from '../canvas-pills/canvasPillsReducer';
import { Dictionary } from 'lodash';
import { useCalculateThirdPartyNodes } from './calculate-nodes/useCalculateThirdPartyNodes';
import { useCalculateRecordTypeNodes } from './calculate-nodes/useCalculateRecordTypeNodes';
import { useCalculateSfFunnelsNodes } from './calculate-nodes/useCalculateSfFunnelsNodes';
import { useCalculateHubspotNodes } from './calculate-nodes/useCalculateHubspotTypeNodes';
import { useCalculateMetadataElementStandaloneNodes } from './calculate-nodes/useCalculateMetadataElementStandaloneNodes';
import { useCalculateSectionNodes } from './calculate-nodes/useCalculateSectionNodes';
import { FunnelStatus } from '../../types/enums/FunnelStatus';
import { SfFunnelMoreButtonProps } from './types';
import { CrmOrg } from '../../types/CrmOrgs';
import { useCalculateStickyNotesNodes } from './calculate-nodes/useCalculateStickyNotesNodes';
import { useCalculateTextNodes } from './calculate-nodes/useCalculateTextNodes';

export const TRANSIENT_GROUP_ID = 'TRANSIENT_GROUP';

export const useFunnelMapElements = ({
  funnelMap,
  transientFunnel,
  renderZeroCountPills,
  visibilityMap,
  pills,
  objectTypesByName,
  editingThirdPartyStepId,
  stagesWithSparkleIcon,
  mapForecastCategoryValueToLabel,
  crmOrgs,
  filterSfPillsByCrmOrgId,
  funnelStatusMap,
  sfFunnelMoreButton,
}: {
  funnelMap: FunnelMap;
  visibilityMap: VisibilityMap;
  transientFunnel?: TransientFunnel;
  renderZeroCountPills?: boolean;
  pills?: Partial<PillsMap>;
  objectTypesByName: Dictionary<ObjectTypeName>;
  editingThirdPartyStepId?: string;
  stagesWithSparkleIcon?: string[];
  mapForecastCategoryValueToLabel?: (value: string) => string;
  crmOrgs?: CrmOrg[];
  filterSfPillsByCrmOrgId?: string;
  funnelStatusMap?: Dictionary<FunnelStatus>;
  sfFunnelMoreButton?: SfFunnelMoreButtonProps;
}) => {
  const hubspotNodes = useCalculateHubspotNodes({
    funnelMap,
    pills,
    visibilityMap,
  });

  const sfFunnelNodes = useCalculateSfFunnelsNodes({
    funnelMap,
    visibilityMap,
    renderZeroCountPills,
    pills,
    transientFunnel,
    objectTypesByName,
    stagesWithSparkleIcon,
    mapForecastCategoryValueToLabel,
    crmOrgs,
    filterSfPillsByCrmOrgId,
    funnelStatusMap,
    sfFunnelMoreButton,
  });
  const thirdPartyNodes = useCalculateThirdPartyNodes({
    funnelMap,
    transientFunnel,
    editingThirdPartyStepId,
    stagesWithSparkleIcon,
  });
  const recordTypeNodes = useCalculateRecordTypeNodes({
    funnelMap,
    objectTypesByName,
    renderZeroCountPills,
    pills,
    visibilityMap,
  });

  const metadataElementStandaloneNodes = useCalculateMetadataElementStandaloneNodes({
    funnelMap,
    crmOrgs,
    visibilityMap,
  });

  const sectionNodes = useCalculateSectionNodes({
    funnelMap,
  });

  const { stickyNotesNodes } = useCalculateStickyNotesNodes({
    stickyNotesMap: funnelMap.stickyNotes,
    transientFunnel,
  });

  const { textNodes } = useCalculateTextNodes({
    textsMap: funnelMap.texts,
    transientFunnel,
  });

  return useMemo(() => {
    const sweepNodes = [
      ...hubspotNodes.nodes,
      ...sfFunnelNodes.nodes,
      ...thirdPartyNodes.nodes,
      ...recordTypeNodes.nodes,
    ];
    const sweepSections = [...sectionNodes.nodes];
    const sweepEdges = [
      ...hubspotNodes.edges,
      ...sfFunnelNodes.edges,
      ...thirdPartyNodes.edges,
      ...recordTypeNodes.edges,
      ...metadataElementStandaloneNodes.edges,
    ];
    const sweepGroups = [
      ...hubspotNodes.groups,
      ...sfFunnelNodes.groups,
      ...thirdPartyNodes.groups,
      ...recordTypeNodes.groups,
    ];

    const sweepStandaloneNodes = [...metadataElementStandaloneNodes.standaloneNodes];

    const sweepStickyNotesNodes = [...stickyNotesNodes];

    const sweepTextNodes = [...textNodes];

    return {
      sweepGroups,
      sweepEdges,
      sweepNodes,
      sweepStandaloneNodes,
      sweepSections,
      sweepStickyNotesNodes,
      sweepTextNodes,
    };
  }, [
    hubspotNodes.nodes,
    hubspotNodes.edges,
    hubspotNodes.groups,
    sfFunnelNodes.nodes,
    sfFunnelNodes.edges,
    sfFunnelNodes.groups,
    thirdPartyNodes.nodes,
    thirdPartyNodes.edges,
    thirdPartyNodes.groups,
    recordTypeNodes.nodes,
    recordTypeNodes.edges,
    recordTypeNodes.groups,
    metadataElementStandaloneNodes.standaloneNodes,
    metadataElementStandaloneNodes.edges,
    sectionNodes.nodes,
    stickyNotesNodes,
    textNodes,
  ]);
};
